/**
 * Financial Aid Application Entity
 *
 * Aggregate root for financial aid requests.
 * Links to an existing Application via applicantId.
 *
 * Using Zod 4.1.9 API
 */
import { z } from 'zod';
import { ResourceId } from '@triedge/domain-core/types';
import {
  FinancialAidApplicationId,
  FinancialAidCode,
  ApplicantId,
  AcademicYear,
  UserId,
} from '../types/branded';
import {
  FinancialAidStatus,
  ConfidenceLevel,
  ReviewFlagType,
  FlagSeverity,
  DecisionType,
  FinancialAidFamilyMatchType,
  FinancialAidHydrationSource,
} from '../types/enums';
import {
  AdditionalInformationSchema,
  AssetsSchema,
  BusinessOwnershipSchema,
  CertificationSchema,
  DocumentChecklistSchema,
  EmploymentInformationSchema,
  FinancialAidSectionsSchema,
  FamilyInformationSchema,
  IncomeSourcesSchema,
  LiabilitiesSchema,
  MajorExpendituresSchema,
  MonthlyExpensesSchema,
  OtherTuitionObligationsSchema,
  DraftSecondaryParentGuardianInfoSchema,
  ParentGuardianInfoSchema,
  RealEstateSchema,
  ReviewSummarySchema,
  SpecialCircumstancesSchema,
  VehiclesSchema,
  YstStudentEntrySchema,
} from '../value-objects/financial-aid';

export const FinancialAidAssignedStaffUserId = z.string()
  .trim()
  .min(1)
  .uuid({
    message: 'Financial aid assigned staff user id must be a valid staff user UUID',
  })
  .brand('FinancialAidAssignedStaffUserId');
export type FinancialAidAssignedStaffUserId = z.infer<typeof FinancialAidAssignedStaffUserId>;

export const isFinancialAidAssignedStaffUserId = (
  value: unknown,
): value is FinancialAidAssignedStaffUserId => (
  FinancialAidAssignedStaffUserId.safeParse(value).success
);

// ============================================================================
// FA-008: Generated Document (for award/denial letters and staff report artifacts)
// ============================================================================

export const FinancialAidReportVariantSchema = z.enum(['summary', 'full', 'yst']);
export type FinancialAidReportVariant = z.infer<typeof FinancialAidReportVariantSchema>;

export const GeneratedDocumentTypeSchema = z.enum([
  'award_letter',
  'denial_letter',
  'financial_aid_summary_report',
  'financial_aid_full_report',
  'financial_aid_yst_report',
]);
export type GeneratedDocumentType = z.infer<typeof GeneratedDocumentTypeSchema>;

export const GeneratedDocumentAppendixSourceStatusSchema = z.enum([
  'included',
  'pending',
  'unsupported',
  'missing',
  'failed',
]);
export type GeneratedDocumentAppendixSourceStatus =
  z.infer<typeof GeneratedDocumentAppendixSourceStatusSchema>;

export const GeneratedDocumentAppendixSourceSchema = z.object({
  documentId: z.string().nullable().optional(),
  documentType: z.string().min(1),
  fileName: z.string().nullable().optional(),
  contentType: z.string().nullable().optional(),
  fileSizeBytes: z.number().int().nonnegative().nullable().optional(),
  status: GeneratedDocumentAppendixSourceStatusSchema,
  message: z.string().nullable().optional(),
});
export type GeneratedDocumentAppendixSource = z.infer<typeof GeneratedDocumentAppendixSourceSchema>;

export const GeneratedDocumentAppendixMetadataSchema = z.object({
  includedCount: z.number().int().nonnegative(),
  omittedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  sources: z.array(GeneratedDocumentAppendixSourceSchema).default([]),
});
export type GeneratedDocumentAppendixMetadata = z.infer<typeof GeneratedDocumentAppendixMetadataSchema>;

export const GeneratedDocumentArtifactMetadataSchema = z.object({
  storageKey: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().min(1),
  fileSizeBytes: z.number().int().nonnegative(),
  generatedAt: z.number().int(),
  generatedBy: UserId,
  reportVariant: FinancialAidReportVariantSchema,
  reportAudience: z.enum(['parent', 'staff']).optional(),
  parentReportRedactionVersion: z.number().int().positive().optional(),
  appendix: GeneratedDocumentAppendixMetadataSchema.optional(),
});
export type GeneratedDocumentArtifactMetadata = z.infer<typeof GeneratedDocumentArtifactMetadataSchema>;

const reportGeneratedDocumentTypes = new Set([
  'financial_aid_summary_report',
  'financial_aid_full_report',
  'financial_aid_yst_report',
]);

export const GeneratedDocumentSchema = z.object({
  id: z.string().uuid(),
  type: GeneratedDocumentTypeSchema,
  resourceId: ResourceId,
  generatedAt: z.number().int(),
  generatedBy: UserId,
  artifact: GeneratedDocumentArtifactMetadataSchema.optional(),
}).superRefine((document, context) => {
  if (!reportGeneratedDocumentTypes.has(document.type)) {
    return;
  }
  if (!document.artifact) {
    context.addIssue({
      code: 'custom',
      message: 'Financial Aid report artifacts require private storage metadata.',
      path: ['artifact'],
    });
    return;
  }
  if (
    (document.type === 'financial_aid_summary_report' && document.artifact.reportVariant !== 'summary')
    || (document.type === 'financial_aid_full_report' && document.artifact.reportVariant !== 'full')
    || (document.type === 'financial_aid_yst_report' && document.artifact.reportVariant !== 'yst')
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Financial Aid report artifact type must match its report variant.',
      path: ['artifact', 'reportVariant'],
    });
  }
});
export type GeneratedDocument = z.infer<typeof GeneratedDocumentSchema>;

// ============================================================================
// Review Flag (nested value object)
// ============================================================================

export const ReviewFlagSchema = z.object({
  id: z.string().uuid(),
  type: ReviewFlagType,
  severity: FlagSeverity,
  description: z.string(),
  triggerCondition: z.string(),
  isResolved: z.boolean().default(false),
  resolvedAt: z.number().int().nullable().optional(),
  resolvedBy: UserId.nullable().optional(),
  resolutionNote: z.string().nullable().optional(),
  createdAt: z.number().int(),
});
export type ReviewFlag = z.infer<typeof ReviewFlagSchema>;

// ============================================================================
// Comparable Family (for recommendation)
// ============================================================================

export const ComparableFamilySchema = z.object({
  academicYear: AcademicYear,
  incomePerChild: z.number().nonnegative(),
  childCount: z.number().int().positive(),
  tuitionBurden: z.number().nonnegative(),
  expenseRatio: z.number().nonnegative(),
  awardedScholarshipPercent: z.number().min(0).max(100),
  similarityScore: z.number().min(0).max(1),
});
export type ComparableFamily = z.infer<typeof ComparableFamilySchema>;

// ============================================================================
// Scholarship Recommendation
// ============================================================================

export const ScholarshipRecommendationWeightsSchema = z.object({
  incomePerChild: z.number(),
  childCount: z.number(),
  tuitionBurden: z.number(),
  expenseRatio: z.number(),
});
export type ScholarshipRecommendationWeights = z.infer<typeof ScholarshipRecommendationWeightsSchema>;

export const PreviousRecommendationSchema = z.object({
  recommendedPercent: z.number().min(0).max(100),
  calculatedAt: z.number().int(),
});
export type PreviousRecommendation = z.infer<typeof PreviousRecommendationSchema>;

export const ScholarshipRecommendationSchema = z.object({
  id: z.string().uuid(),
  recommendedPercent: z.number().min(0).max(100),
  recommendedAmount: z.number().nonnegative(),
  confidenceLevel: ConfidenceLevel,
  comparableFamilies: z.array(ComparableFamilySchema).length(5),
  weights: ScholarshipRecommendationWeightsSchema,
  calculatedAt: z.number().int(),
  previousRecommendations: z.array(PreviousRecommendationSchema).default([]),
});
export type ScholarshipRecommendation = z.infer<typeof ScholarshipRecommendationSchema>;

// ============================================================================
// Staff Decision (Discriminated Union)
// ============================================================================

const BaseDecisionSchema = z.object({
  id: z.string().uuid(),
  decidedBy: UserId,
  decidedByName: z.string().nullable().optional(),
  decidedAt: z.number().int(),
});

const ApproveDecisionSchema = BaseDecisionSchema.extend({
  decisionType: z.literal('approve'),
  awardedPercent: z.number().min(0).max(100),
  awardedAmount: z.number().nonnegative(),
});

const OverrideDecisionSchema = BaseDecisionSchema.extend({
  decisionType: z.literal('override'),
  awardedPercent: z.number().min(0).max(100),
  awardedAmount: z.number().nonnegative(),
  overrideJustification: z.string().min(1),
});

const RequestMoreInfoDecisionSchema = BaseDecisionSchema.extend({
  decisionType: z.literal('request_more_info'),
  requestedInfo: z.string().min(1),
  responseDeadline: z.number().int(),
  moreInfoRequestId: z.string().trim().min(1).optional(),
});

const DenyDecisionSchema = BaseDecisionSchema.extend({
  decisionType: z.literal('deny'),
  denialReason: z.string().min(1),
  denialExplanation: z.string().nullable().optional(),
});

export const StaffDecisionSchema = z.discriminatedUnion('decisionType', [
  ApproveDecisionSchema,
  OverrideDecisionSchema,
  RequestMoreInfoDecisionSchema,
  DenyDecisionSchema,
]);
export type StaffDecision = z.infer<typeof StaffDecisionSchema>;
export type ApproveDecision = z.infer<typeof ApproveDecisionSchema>;
export type OverrideDecision = z.infer<typeof OverrideDecisionSchema>;
export type RequestMoreInfoDecision = z.infer<typeof RequestMoreInfoDecisionSchema>;
export type DenyDecision = z.infer<typeof DenyDecisionSchema>;

// ============================================================================
// Final Award (after acceptance)
// ============================================================================

export const FinalAwardSchema = z.object({
  scholarshipPercent: z.number().min(0).max(100),
  scholarshipAmount: z.number().nonnegative(),
  familyResponsibility: z.number().nonnegative(),
  acceptedAt: z.number().int().nullable().optional(),
  acceptedBy: z.string().nullable().optional(),
});
export type FinalAward = z.infer<typeof FinalAwardSchema>;

// ============================================================================
// Superseded Decision (preserved history when an application is reopened)
// ============================================================================

/**
 * A staff decision that was superseded when an administrator reopened the
 * application. The decision itself is never destroyed: it is moved verbatim
 * into `supersededDecisions` together with the award that had been communicated,
 * the status it was superseded from, and who reopened it and why.
 */
export const SupersededFinancialAidDecisionSchema = z.object({
  id: z.string().uuid(),
  decision: StaffDecisionSchema,
  finalAward: FinalAwardSchema.nullable().optional(),
  supersededFromStatus: FinancialAidStatus,
  supersededAt: z.number().int(),
  supersededBy: z.string().trim().min(1),
  supersededByName: z.string().nullable().optional(),
  supersededByRole: z.string().trim().min(1),
  reopenReason: z.string().trim().min(1),
  statusHistoryAuditId: z.string().uuid().nullable().optional(),
});
export type SupersededFinancialAidDecision = z.infer<typeof SupersededFinancialAidDecisionSchema>;

// ============================================================================
// Calculated Metrics (persisted for reporting)
// ============================================================================

export const CalculatedMetricsSchema = z.object({
  totalAnnualIncome: z.number().nonnegative(),
  totalAssets: z.number().nonnegative(),
  totalLiabilities: z.number().nonnegative(),
  netWorth: z.number(),
  incomePerEligibleChild: z.number().nonnegative(),
  eligibleChildCount: z.number().int().nonnegative(),
  totalChildCount: z.number().int().nonnegative(),
  ystEligibleTuition: z.number().nonnegative(),
  ystTotalTuition: z.number().nonnegative(),
  ystTotalFees: z.number().nonnegative(),
  otherSchoolTuition: z.number().nonnegative(),
  totalTuitionBurden: z.number().nonnegative(),
  tuitionAsPercentOfIncome: z.number().nonnegative(),
  debtToIncomeRatio: z.number().nonnegative(),
  expenseToIncomeRatio: z.number().nonnegative(),
  totalMonthlyExpenses: z.number().nonnegative(),
  calculatedAt: z.number().int(),
  previousMetrics: z.array(z.object({
    totalAnnualIncome: z.number().nonnegative(),
    incomePerEligibleChild: z.number().nonnegative(),
    calculatedAt: z.number().int(),
  })).default([]),
});
export type CalculatedMetrics = z.infer<typeof CalculatedMetricsSchema>;

// ============================================================================
// Invite + Linking + Hydration Metadata
// ============================================================================

export const FinancialAidInviteSchema = z.object({
  email: z.string().email(),
  lastName: z.string().trim().min(1).nullable().optional(),
  token: z.string().uuid(),
  invitedAt: z.number().int(),
  invitedBy: UserId.nullable().optional(),
  lastSentAt: z.number().int(),
  resendCount: z.number().int().nonnegative().default(0),
  lastSendOperationId: z.string().trim().min(1).nullable().optional(),
  lastSendOperationKind: z.string().trim().min(1).nullable().optional(),
  lastSendOperationCreatedAt: z.number().int().nullable().optional(),
  lastSendAuditRecordedAt: z.number().int().nullable().optional(),
  lastSendAuditOutboxId: z.string().trim().min(1).nullable().optional(),
  lastSendAuditCorrelationId: z.string().trim().min(1).nullable().optional(),
  lastOpenedAt: z.number().int().nullable().optional(),
  lastCopiedAt: z.number().int().nullable().optional(),
  claimedEmail: z.string().email().nullable().optional(),
  claimedAt: z.number().int().nullable().optional(),
  claimedBy: UserId.nullable().optional(),
});
export type FinancialAidInvite = z.infer<typeof FinancialAidInviteSchema>;

export const FinancialAidFamilyLinkSchema = z.object({
  familyId: z.string().trim().min(1).nullable().optional(),
  familyName: z.string().nullable().optional(),
  applicantId: ApplicantId.nullable().optional(),
  matchType: FinancialAidFamilyMatchType.default('none'),
  matchedAt: z.number().int().nullable().optional(),
});
export type FinancialAidFamilyLink = z.infer<typeof FinancialAidFamilyLinkSchema>;

export const FinancialAidHydrationSchema = z.object({
  demographicsSource: FinancialAidHydrationSource.default('none'),
  financialsSource: FinancialAidHydrationSource.default('none'),
  sourceApplicationId: FinancialAidApplicationId.nullable().optional(),
  sourceAcademicYear: AcademicYear.nullable().optional(),
  hydratedAt: z.number().int().nullable().optional(),
  bannerMessage: z.string().nullable().optional(),
});
export type FinancialAidHydration = z.infer<typeof FinancialAidHydrationSchema>;

export const FinancialAidEntrySourceSchema = z.enum(['generic_open_cycle']);
export type FinancialAidEntrySource = z.infer<typeof FinancialAidEntrySourceSchema>;

export const FinancialAidLinkActivityEventTypeSchema = z.enum([
  'invite_sent',
  'link_copied',
  'invite_opened',
  'invite_claimed',
]);
export type FinancialAidLinkActivityEventType = z.infer<typeof FinancialAidLinkActivityEventTypeSchema>;

export const FinancialAidLinkActivityAuditActionSchema = z.enum([
  'fa_invite_sent',
  'fa_link_copied',
  'fa_invite_opened',
  'fa_invite_claimed',
]);
export type FinancialAidLinkActivityAuditAction = z.infer<typeof FinancialAidLinkActivityAuditActionSchema>;

export const FinancialAidLinkActivityEventSchema = z.object({
  id: z.string().uuid(),
  applicationId: FinancialAidApplicationId.nullable().optional(),
  eventType: FinancialAidLinkActivityEventTypeSchema,
  auditAction: FinancialAidLinkActivityAuditActionSchema,
  occurredAt: z.number().int(),
  actorId: z.string().nullable().optional(),
  actorRole: z.string().nullable().optional(),
  recipientEmail: z.string().email().nullable().optional(),
  familyId: z.string().trim().min(1).nullable().optional(),
  academicYear: AcademicYear,
  metadata: z.record(z.string(), z.unknown()).default({}).optional(),
});
export type FinancialAidLinkActivityEvent = z.infer<typeof FinancialAidLinkActivityEventSchema>;

const financialAidDateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

const isRealFinancialAidDate = (value: string): boolean => {
  if (!financialAidDateOnlyPattern.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const instant = new Date(Date.UTC(year, month - 1, day));
  return (
    instant.getUTCFullYear() === year &&
    instant.getUTCMonth() === month - 1 &&
    instant.getUTCDate() === day
  );
};

export const FinancialAidDeadlineDateSchema = z
  .string()
  .superRefine((value, context) => {
    if (!isRealFinancialAidDate(value)) {
      context.addIssue({
        code: 'custom',
        message: 'Deadline must be a real date in YYYY-MM-DD format',
      });
    }
  });
export type FinancialAidDeadlineDate = z.infer<
  typeof FinancialAidDeadlineDateSchema
>;

export const FinancialAidDeadlineExtensionSchema = z.object({
  id: z.string().uuid(),
  applicationId: FinancialAidApplicationId,
  academicYear: AcademicYear,
  cycleHardCutoffDeadline: FinancialAidDeadlineDateSchema,
  extendedDeadline: FinancialAidDeadlineDateSchema,
  reason: z.string().trim().min(1).max(4000),
  grantedByActorId: z.string().trim().min(1),
  grantedByActorRole: z.enum(['admin', 'owner', 'super-administrator']),
  grantedByActorName: z.string().trim().min(1).nullable().optional(),
  grantedAt: z.number().int().nonnegative(),
});
export type FinancialAidDeadlineExtension = z.infer<
  typeof FinancialAidDeadlineExtensionSchema
>;

export const FinancialAidSubmissionTimingSchema = z.enum([
  'on_time',
  'late',
  'extended',
]);
export type FinancialAidSubmissionTiming = z.infer<
  typeof FinancialAidSubmissionTimingSchema
>;

// ============================================================================
// Financial Aid Application Entity (Aggregate Root)
// ============================================================================

export const FinancialAidApplicationSchema = z.object({
  id: FinancialAidApplicationId,
  tenantId: z.string().trim().min(1).nullable().optional(),
  code: FinancialAidCode,
  applicantId: ApplicantId,
  academicYear: AcademicYear,
  status: FinancialAidStatus,
  sections: FinancialAidSectionsSchema,
  metrics: CalculatedMetricsSchema.nullable().optional(),
  recommendation: ScholarshipRecommendationSchema.nullable().optional(),
  flags: z.array(ReviewFlagSchema).default([]),
  decision: StaffDecisionSchema.nullable().optional(),
  supersededDecisions: z.array(SupersededFinancialAidDecisionSchema).default([]).optional(),
  generatedDocuments: z.array(GeneratedDocumentSchema).default([]),
  finalAward: FinalAwardSchema.nullable().optional(),
  invite: FinancialAidInviteSchema.nullable().optional(),
  familyLink: FinancialAidFamilyLinkSchema.nullable().optional(),
  hydration: FinancialAidHydrationSchema.nullable().optional(),
  entrySource: FinancialAidEntrySourceSchema.nullable().optional(),
  linkActivityEvents: z.array(FinancialAidLinkActivityEventSchema).default([]).optional(),
  assignedTo: FinancialAidAssignedStaffUserId.nullable().optional(),
  assigneeName: z.string().nullable().optional(),
  deadlineExtensions: z.array(FinancialAidDeadlineExtensionSchema).default([]),
  submissionTiming: FinancialAidSubmissionTimingSchema.nullable().optional(),
  isLateSubmission: z.boolean().default(false),
  lateFee: z.number().nonnegative().default(0),
  registrationFeePaid: z.boolean().default(false),
  lastActivityAt: z.number().int().nullable().optional(),
  requestedInfo: z.string().nullable().optional(),
  responseDeadline: z.number().int().nullable().optional(),
  moreInfoRequestId: z.string().nullable().optional(),
  lastMoreInfoResponseRequestId: z.string().nullable().optional(),
  submittedAt: z.number().int().nullable().optional(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type FinancialAidApplication = z.infer<typeof FinancialAidApplicationSchema>;

// ============================================================================
// Input Types
// ============================================================================

export const CreateFinancialAidApplicationInputSchema = z.object({
  applicantId: ApplicantId,
  academicYear: AcademicYear,
  sections: FinancialAidSectionsSchema.partial(),
});
export type CreateFinancialAidApplicationInput = z.infer<
  typeof CreateFinancialAidApplicationInputSchema
>;

export const GrantFinancialAidDeadlineExtensionInputSchema = z
  .object({
    applicationId: FinancialAidApplicationId,
    extendedDeadline: FinancialAidDeadlineDateSchema,
    reason: z.string().trim().min(1).max(4000),
  })
  .strict();
export type GrantFinancialAidDeadlineExtensionInput = z.infer<
  typeof GrantFinancialAidDeadlineExtensionInputSchema
>;

export const UpdateFinancialAidSectionsSchema = z
  .object({
    familyInformation: FamilyInformationSchema.partial(),
    parent1: ParentGuardianInfoSchema.partial(),
    parent2: DraftSecondaryParentGuardianInfoSchema.partial().nullable(),
    employment: EmploymentInformationSchema.partial(),
    income: IncomeSourcesSchema.partial(),
    assets: AssetsSchema.partial(),
    liabilities: LiabilitiesSchema.partial(),
    expenses: MonthlyExpensesSchema.partial(),
    specialCircumstances: SpecialCircumstancesSchema.partial(),
    otherTuition: OtherTuitionObligationsSchema.partial(),
    majorExpenditures: MajorExpendituresSchema.partial(),
    businessOwnership: BusinessOwnershipSchema.partial(),
    realEstate: RealEstateSchema.partial(),
    vehicles: VehiclesSchema.partial(),
    additionalInfo: AdditionalInformationSchema.partial(),
    documentChecklist: DocumentChecklistSchema.partial(),
    certification: CertificationSchema.nullable(),
    reviewSummary: ReviewSummarySchema.nullable(),
    ystStudents: z.array(YstStudentEntrySchema),
  })
  .partial();
export type UpdateFinancialAidSections = z.infer<
  typeof UpdateFinancialAidSectionsSchema
>;

export const UpdateFinancialAidApplicationInputSchema = z.object({
  id: FinancialAidApplicationId,
  sections: UpdateFinancialAidSectionsSchema,
  registrationFeePaid: z.boolean().optional(),
  correctionMode: z.literal('staff_correction').optional(),
  correctionReason: z.string().trim().min(1).max(4000).optional(),
  expectedUpdatedAt: z.number().int().optional(),
});
export type UpdateFinancialAidApplicationInput = z.infer<typeof UpdateFinancialAidApplicationInputSchema>;

export const SubmitFinancialAidApplicationInputSchema = z.object({
  id: FinancialAidApplicationId,
  certification: z.object({
    certifiedAccurate: z.literal(true),
    certifiedBy: z.string().min(1),
    electronicSignature: z.string().min(1),
    agreedToTerms: z.literal(true),
  }).strict(),
}).strict();
export type SubmitFinancialAidApplicationInput = z.infer<typeof SubmitFinancialAidApplicationInputSchema>;

export const MakeFinancialAidDecisionInputSchema = z.object({
  financialAidApplicationId: FinancialAidApplicationId,
  expectedUpdatedAt: z.number().int(),
  decisionType: DecisionType,
  awardedPercent: z.number().min(0).max(100).optional(),
  overrideJustification: z.string().optional(),
  requestedInfo: z.string().optional(),
  responseDeadlineDays: z.number().int().positive().optional(),
  denialReason: z.string().optional(),
  denialExplanation: z.string().optional(),
  sendExternalParentEmail: z.boolean().optional(),
  externalParentEmailApproved: z.boolean().optional(),
});
export type MakeFinancialAidDecisionInput = z.infer<typeof MakeFinancialAidDecisionInputSchema>;

export const AcceptFinancialAidAwardInputSchema = z.object({
  financialAidApplicationId: FinancialAidApplicationId,
  acceptedBy: z.string().min(1),
  accepted: z.boolean(),
});
export type AcceptFinancialAidAwardInput = z.infer<typeof AcceptFinancialAidAwardInputSchema>;

export const FinancialAidFamilyLookupInputSchema = z.object({
  email: z.string().email().optional(),
  lastName: z.string().trim().min(1).optional(),
  familyName: z.string().trim().min(1).optional(),
  academicYear: AcademicYear.optional(),
  limit: z.number().int().positive().max(10).optional(),
});
export type FinancialAidFamilyLookupInput = z.infer<typeof FinancialAidFamilyLookupInputSchema>;

export const InviteFinancialAidApplicationInputSchema = z.object({
  academicYear: AcademicYear,
  primaryEmail: z.string().email(),
  lastName: z.string().trim().min(1).optional(),
  familyId: z.string().uuid().optional(),
  familyName: z.string().trim().min(1).optional(),
  applicantId: ApplicantId.optional(),
});
export type InviteFinancialAidApplicationInput = z.infer<typeof InviteFinancialAidApplicationInputSchema>;

export const CopyFinancialAidInviteLinkInputSchema = z.object({
  applicationId: FinancialAidApplicationId.optional(),
  academicYear: AcademicYear.optional(),
}).refine((input) => Boolean(input.applicationId || input.academicYear), {
  message: 'applicationId or academicYear is required',
});
export type CopyFinancialAidInviteLinkInput = z.infer<typeof CopyFinancialAidInviteLinkInputSchema>;

export const ResendFinancialAidInviteInputSchema = z.object({
  applicationId: FinancialAidApplicationId,
});
export type ResendFinancialAidInviteInput = z.infer<typeof ResendFinancialAidInviteInputSchema>;

export const ClaimFinancialAidInviteInputSchema = z.object({
  token: z.string().uuid(),
  applicantId: ApplicantId.optional(),
});
export type ClaimFinancialAidInviteInput = z.infer<typeof ClaimFinancialAidInviteInputSchema>;

// ============================================================================
// Filter Types
// ============================================================================

export const FinancialAidApplicationFilterSchema = z.object({
  status: z.array(FinancialAidStatus).optional(),
  academicYear: AcademicYear.optional(),
  assignedTo: FinancialAidAssignedStaffUserId.nullable().optional(),
  hasHighSeverityFlags: z.boolean().optional(),
  confidenceLevel: ConfidenceLevel.optional(),
  submittedAfter: z.number().int().optional(),
  submittedBefore: z.number().int().optional(),
  search: z.string().optional(),
});
export type FinancialAidApplicationFilter = z.infer<typeof FinancialAidApplicationFilterSchema>;

// ============================================================================
// Helpers
// ============================================================================

export const isGradeEligibleForScholarship = (grade: string): boolean => {
  const ineligibleGrades = ['nursery', 'pre_k', 'kindergarten'];
  return !ineligibleGrades.includes(grade);
};

export const generateFinancialAidCode = (year: number, sequence: number): string => {
  return `FA-${year}-${sequence.toString().padStart(5, '0')}`;
};

export interface FinancialAidSubmissionDeadlineContext {
  onTimeDeadline: FinancialAidDeadlineDate;
  hardCutoffDeadline: FinancialAidDeadlineDate;
  onTimeDeadlineEndExclusiveMs: number;
  hardCutoffDeadlineEndExclusiveMs: number;
  extension?: FinancialAidDeadlineExtension | null;
  extensionDeadlineEndExclusiveMs?: number | null;
}

export interface FinancialAidSubmissionDeadlineStatus {
  isLate: boolean;
  isPastDeadline: boolean;
  isExtended: boolean;
  submissionTiming: FinancialAidSubmissionTiming;
}

export const resolveFinancialAidSubmissionDeadlineStatus = (
  submissionDate: Date,
  context: FinancialAidSubmissionDeadlineContext,
): FinancialAidSubmissionDeadlineStatus => {
  const submissionMs = submissionDate.getTime();
  const onTimeDeadlineEndExclusiveMs = Number(
    context.onTimeDeadlineEndExclusiveMs,
  );
  const hardCutoffDeadlineEndExclusiveMs = Number(
    context.hardCutoffDeadlineEndExclusiveMs,
  );
  if (
    !Number.isFinite(submissionMs) ||
    !Number.isFinite(onTimeDeadlineEndExclusiveMs) ||
    !Number.isFinite(hardCutoffDeadlineEndExclusiveMs)
  ) {
    throw new Error(
      'Configured financial aid submission deadlines are invalid',
    );
  }

  const extensionMatchesConfiguredCutoff = Boolean(
    context.extension &&
      context.extension.cycleHardCutoffDeadline === context.hardCutoffDeadline,
  );
  const extensionDeadlineEndExclusiveMs = extensionMatchesConfiguredCutoff
    ? Number(context.extensionDeadlineEndExclusiveMs)
    : Number.NaN;
  const hasActiveExtensionBoundary =
    Number.isFinite(extensionDeadlineEndExclusiveMs) &&
    extensionDeadlineEndExclusiveMs > hardCutoffDeadlineEndExclusiveMs;
  const effectiveDeadlineEndExclusiveMs = hasActiveExtensionBoundary
    ? extensionDeadlineEndExclusiveMs
    : hardCutoffDeadlineEndExclusiveMs;
  const isExtended =
    hasActiveExtensionBoundary &&
    submissionMs >= hardCutoffDeadlineEndExclusiveMs &&
    submissionMs < extensionDeadlineEndExclusiveMs;
  const isPastDeadline = submissionMs >= effectiveDeadlineEndExclusiveMs;
  const isLate =
    submissionMs >= onTimeDeadlineEndExclusiveMs && !isPastDeadline;

  return {
    isLate,
    isPastDeadline,
    isExtended,
    submissionTiming: isExtended ? 'extended' : isLate ? 'late' : 'on_time',
  };
};

export const isLateSubmission = (
  submissionDate: Date,
  context: FinancialAidSubmissionDeadlineContext,
): boolean =>
  resolveFinancialAidSubmissionDeadlineStatus(submissionDate, context).isLate;

export const isPastDeadline = (
  submissionDate: Date,
  context: FinancialAidSubmissionDeadlineContext,
): boolean =>
  resolveFinancialAidSubmissionDeadlineStatus(submissionDate, context)
    .isPastDeadline;
